import { useUser } from "@clerk/tanstack-react-start";
import {
  createFileRoute,
  notFound,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { Breadcrumb } from "~/components/ui/breadcrumb";
import { ButtonLink } from "~/components/ui/button";
import { WheelActionBar } from "~/components/wheel/results/action-bar";
import { CoachLine } from "~/components/wheel/results/coach-line";
import { CompassIcon } from "~/components/wheel/results/icons";
import { WheelResultsLayout } from "~/components/wheel/results/results-layout";
import { ShareWithCoach } from "~/components/wheel/results/share-with-coach";
import {
  WHEEL_WEDGE_COLORS,
  WHEEL_WEDGE_INK,
} from "~/components/wheel/wheel-colors";
import { trackWheelNextStepClicked } from "~/lib/monitoring/track-event";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";
import {
  inferIntakeTriggerFromScores,
  selectWeakAreas,
  type ResolvedWheelSuggestion,
} from "~/lib/shared/wheel";
import {
  parseEngagementSearch,
  wheelRequestPath,
} from "~/lib/shared/wheel-request";

export const Route = createFileRoute("/_public/wheel-of-life/results/$id")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { claim: "1" | undefined; engagement?: string } => {
    const engagement = parseEngagementSearch(search.engagement);
    return {
      claim: search.claim === "1" ? "1" : undefined,
      ...(engagement && { engagement }),
    };
  },
  loader: async ({ params }) => {
    const {
      getWheelSessionFn,
      generateWheelSuggestionsFn,
      resolveWheelSuggestionsFn,
    } = await import("~/lib/functions/wheel");
    const row = await getWheelSessionFn({ data: { id: params.id } });
    if (!row) throw notFound();
    // 302, not 301: the session leaves its coach's page when the page is off.
    if (row.coachUsername) {
      throw redirect({
        to: "/$username/wheel-of-life/results/$id",
        params: { username: row.coachUsername, id: params.id },
        statusCode: 302,
      });
    }

    let suggestions = row.suggestions;
    if (!suggestions) {
      try {
        suggestions = await generateWheelSuggestionsFn({
          data: { id: params.id },
        });
      } catch {
        suggestions = [];
      }
    }

    let resolved: ResolvedWheelSuggestion[] = [];
    if (suggestions && suggestions.length > 0) {
      try {
        resolved = await resolveWheelSuggestionsFn({
          data: { suggestions },
        });
      } catch {
        resolved = [];
      }
    }

    return {
      session: { ...row, suggestions: suggestions ?? [] },
      resolved,
    };
  },
  staticData: { title: "Your Wheel of Life Results" },
  head: ({ params }) => ({
    ...createBrandMeta({
      title: "My Wheel of Life",
      description:
        "See how my eight life areas are stacking up — and where I'm stretched.",
      ogTitle: "My Wheel of Life — Dream Coach Match",
      ogDescription:
        "See how my eight life areas are stacking up — and where I'm stretched.",
      ogImage: `/api/og/wheel/${params.id}`,
      ogType: "website",
      twitterCard: "summary_large_image",
      noindex: true,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Wheel of Life", url: `${BRAND_URL}/wheel-of-life` },
            {
              name: "Your results",
              url: `${BRAND_URL}/wheel-of-life/results/${params.id}`,
            },
          ]),
        ),
      },
    ],
  }),
  component: ResultsPage,
});

function ResultsPage() {
  const { session, resolved } = Route.useLoaderData();
  const params = Route.useParams();
  const search = Route.useSearch();
  const { user, isLoaded } = useUser();
  const router = useRouter();

  useEffect(() => {
    if (!isLoaded || !user) return;
    if (search.claim !== "1") return;
    let cancelled = false;
    (async () => {
      const { claimWheelSessionFn } = await import("~/lib/functions/wheel");
      try {
        await claimWheelSessionFn({ data: { id: params.id } });
      } catch {
        // Already claimed, or by someone else — silent.
      }
      if (cancelled) return;
      if (typeof window !== "undefined") {
        // Drop claim=1 but keep the request's engagement for the share box.
        const kept = search.engagement
          ? `?engagement=${search.engagement}`
          : "";
        window.history.replaceState(null, "", window.location.pathname + kept);
      }
      router.invalidate();
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, user, search.claim, search.engagement, params.id, router]);

  const weak = selectWeakAreas(session.scores);
  const intakeTrigger = inferIntakeTriggerFromScores(session.scores);
  const weakLabels = weak.map((i) => session.labels[i]);
  const claimPath = `/wheel-of-life/results/${params.id}?claim=1${search.engagement ? `&engagement=${search.engagement}` : ""}`;
  const saveHref = session.isClaimed
    ? null
    : user
      ? claimPath
      : `/login?redirect=${encodeURIComponent(claimPath)}`;

  return (
    <WheelResultsLayout
      id={params.id}
      session={session}
      top={
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Wheel of Life", href: "/wheel-of-life" },
            { label: "Your results" },
          ]}
          isBold={false}
        />
      }
      actions={
        <WheelActionBar
          id={params.id}
          pdfHref={`/wheel-of-life/results/${params.id}/print`}
          rescoreHref={
            search.engagement
              ? `${wheelRequestPath(search.engagement)}&from=${params.id}`
              : `/wheel-of-life?from=${params.id}`
          }
          saveHref={saveHref}
        />
      }
    >
      <SuggestionCards labels={session.labels} resolved={resolved} />
      <CoachCta
        intakeTrigger={intakeTrigger}
        weakLabels={weakLabels}
        sessionId={params.id}
      />
      {!search.engagement && (
        <CoachLine userId={user?.id} isAuthLoaded={isLoaded} />
      )}
      {user && search.engagement && (
        <ShareWithCoach
          sessionId={params.id}
          engagementId={search.engagement}
        />
      )}
    </WheelResultsLayout>
  );
}

function SuggestionCards({
  labels,
  resolved,
}: {
  labels: string[];
  resolved: ResolvedWheelSuggestion[];
}) {
  if (resolved.length === 0) return null;
  return (
    <div className="mt-6 space-y-3">
      <h2 className="text-[11px] font-medium tracking-[0.14em] text-neutral-500 uppercase">
        Next steps for your stretched areas
      </h2>
      <div className="space-y-3">
        {resolved.map((s) => {
          const color =
            WHEEL_WEDGE_COLORS[s.categoryIndex % WHEEL_WEDGE_COLORS.length];
          const ink = WHEEL_WEDGE_INK[s.categoryIndex % WHEEL_WEDGE_INK.length];
          return (
            <a
              key={`${s.cihItemType}-${s.cihItemId}`}
              href={s.href}
              className="group relative block overflow-hidden rounded-xl bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.02)] ring-1 ring-neutral-200/80 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.08)] hover:ring-neutral-300"
            >
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 w-1"
                style={{ background: color }}
              />
              <p
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-[0.1em] uppercase"
                style={{
                  background: `color-mix(in oklch, ${color} 14%, white)`,
                  color: ink,
                }}
              >
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: color }}
                />
                {labels[s.categoryIndex]} · {s.score}/10
              </p>
              <p className="mt-3 text-base font-medium text-neutral-900">
                {s.title}
              </p>
              {s.excerpt && (
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">
                  {s.excerpt}
                </p>
              )}
              <p
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium"
                style={{ color: ink }}
              >
                Read more
                <span
                  aria-hidden
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                >
                  ↗
                </span>
              </p>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function CoachCta({
  intakeTrigger,
  weakLabels,
  sessionId: _sessionId,
}: {
  intakeTrigger: string | null;
  weakLabels: string[];
  sessionId: string;
}) {
  if (weakLabels.length === 0) return null;
  const params = new URLSearchParams();
  params.set("source", "wheel-of-life");
  if (intakeTrigger) params.set("trigger", intakeTrigger);
  const dest = `/intake?${params.toString()}`;
  const pair = weakLabels.slice(0, 2).join(" and ");
  return (
    <div className="mt-10 rounded-2xl bg-gradient-to-br from-orange-50 via-amber-50/70 to-orange-100/40 p-7 ring-1 ring-orange-200/60">
      <p className="flex items-center gap-2 text-[11px] font-medium tracking-[0.14em] text-orange-800 uppercase">
        <CompassIcon />
        Turn this reflection into a plan
      </p>
      <p className="mt-3 text-xl leading-snug font-medium text-neutral-900">
        Answer a few more questions about your situation around {pair}, and
        we'll recommend your best next step — a coach, a group program, or our
        Life By Design tool.
      </p>
      <div className="mt-5">
        <ButtonLink
          href={dest}
          variant="default"
          size="lg"
          className="rounded-full px-7 py-3 shadow-[0_10px_28px_-8px_rgba(249,115,22,0.45)]"
          onClick={trackWheelNextStepClicked}
        >
          Find your next step →
        </ButtonLink>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        ~3 minutes · We use your wheel + a few specifics to tailor the
        recommendation.
      </p>
    </div>
  );
}
